<template>
  <div>
    <tool-bar :title="loanAccountNumber"></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <contact-support-item
      :contact-support-actions-event="contactSupportEvent"
      @resetContactSupportEvent="contactSupportEvent=false">
    </contact-support-item>

    <wxc-countdown
      v-if="contactSupportTimerOn"
      v-show="false"
      tpl="{s}"
      :time="contactSupportTime"
      @wxcOnComplete="stopContactSupportTimer"
      @countdownDetails="onContactSupportTimeDetails"
    ></wxc-countdown>

    <wxc-countdown
      v-if="refreshTimerOn"
      v-show="false"
      :time="refreshTime"
      @wxcOnComplete="onRefreshClicked"
    ></wxc-countdown>

    <div class="margin-top-40 margin-left-10 margin-right-10">
      <weex-text class="item-heading">{{processingText}}</weex-text>

      <wxc-button
        text="Refresh"
        size="medium"
        class="horizontal-center margin-top-40"
        type="blue"
        @wxcButtonClicked="onRefreshClicked"
      ></wxc-button>

      <weex-text class="margin-top-20 text-align-center font-weight-bold text-color-gray">-OR-
      </weex-text>

      <div v-if="contactSupportTimerOn" class="margin-top-5">
        <weex-text class="text-align-center text-color-gray font-size-sm-base">
          {{contactSupportTimerMessageDetails}}
        </weex-text>

        <weex-progress
          :width="12"
          :value="progressValue"
          border-color="blue"
          class="horizontal_center margin-left-30 margin-right-30"
        ></weex-progress>
      </div>

      <wxc-button
        v-if="contactSupportTimerOn"
        text="Contact Support"
        :btn-style="contactSupportDisabledButtonStyle"
        :text-style="contactSupportDisabledTextStyle"
        size="medium"
        class="horizontal-center margin-top-20"
      ></wxc-button>

      <wxc-button
        v-if="!contactSupportTimerOn"
        text="Contact Support"
        size="medium"
        class="horizontal-center margin-top-20"
        type="blue"
        @wxcButtonClicked="contactSupportEvent=true"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import ToolBar from "../layout/ToolBar";
import Loading from "../layout/Loading";
import ContactSupportItem from "../layout/ContactSupportItem";
import WeexProgress from "../../../weex-ui/packages/WeexProgress";
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import WxcCountdown from "../../../weex-ui/packages/wxc-countdown/index";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import DataSource from "../../data/DataSource";
import EzUtils from "../../utils/EzUtils";
import ActivityNavigator, { Activities } from "../../utils/ActivityNavigator";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import { ToolbarMenuItems } from "../../utils/ToolbarMenuItems";

const CONTACT_SUPPORT_TIME = 5 * 60 * 1000; // 5 minutes
const CONTACT_SUPPORT_TIME_IN_SECONDS = 5 * 60; // 5 minutes
const REFRESH_TIME = 20 * 1000; // 20 seconds

export default {
  name: "WaitActivity",
  components: {
    ToolBar,
    WxcButton,
    Loading,
    WxcCountdown,
    WeexProgress,
    ContactSupportItem,
    ErrorAlertItem
  },

  data: () => ({
    loading: false,
    networkError: {},
    errorAlertEvent: false,
    contactSupportTimerOn: false,
    contactSupportTime: "",
    refreshTimerOn: false,
    refreshTime: "",
    contactSupportTimerMessageDetails: "",
    progressValue: 100,
    contactSupportEvent: false
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    onToolbarActionSelected(selectedAction) {
      if (ToolbarMenuItems.GO_TO_MERCHANT_APP === selectedAction) {
        this.showRedirectToPartnerDialog();
      }
    },

    onContactSupportTimeDetails(countdownData) {
      const totalRemainingSeconds = countdownData.second;
      const minutes = parseInt((totalRemainingSeconds / 60).toString());
      const seconds = totalRemainingSeconds - minutes * 60;

      const minutesAsString = minutes > 9 ? minutes : "0" + minutes;
      const secondsAsString = seconds > 9 ? seconds : "0" + seconds;

      this.contactSupportTimerMessageDetails = `You can contact support in ${minutesAsString}:${secondsAsString}`;
      this.progressValue = (totalRemainingSeconds / CONTACT_SUPPORT_TIME_IN_SECONDS) * 100;
    },

    onRefreshClicked() {
      this.stopRefreshTimer();
      if (EzUtils.isValidId(LoanScopeDataHolder.getLoanId())) {
        this.getLoanDetailsUsingLoanId();
      } else if (LoanScopeDataHolder.getLoanAccountNumber()) {
        this.getLoanDetailsUsingAccountNumber();
      }
    },

    getLoanDetailsUsingAccountNumber() {
      this.loading = true;
      DataSource.getLoanAccountDetailsByAccountNumber(LoanScopeDataHolder.getLoanAccountNumber())
        .then(loanDetailsRes => {
          this.loading = false;
          if (loanDetailsRes.errorOccurred) {
            this.showError(loanDetailsRes.data);
            this.startRefreshTimer();
          } else {
            LoanScopeDataHolder.setLoanId(loanDetailsRes.data.id);
            this.getLoanDetailsUsingLoanId();
          }
        });
    },

    getLoanDetailsUsingLoanId() {
      this.loading = true;
      DataSource.getEzLoanById(LoanScopeDataHolder.getLoanId())
        .then(ezLoanDataRes => {
          this.loading = false;
          if (ezLoanDataRes.errorOccurred) {
            this.showError(ezLoanDataRes.data);
            this.startRefreshTimer();
          } else {
            LoanScopeDataHolder.setDetailsFromEzLoan(ezLoanDataRes.data);
            if (LoanScopeDataHolder.isLoanRejected()
                || LoanScopeDataHolder.isLoanWithdrawnByApplicant()
            ) {
              this.goToLoanDetails();
            } else {
              this.fetchNextTask();
            }
          }
        });
    },

    fetchNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(
        [],
        LoanScopeDataHolder.isFetchingLoanDetailsFirstTime()
      )
        .then(taskRes => {
          this.loading = false;
          this.startRefreshTimer();
          if (taskRes.errorOccurred) {
            this.showError(taskRes.data);
          }
        });
    },

    startRefreshTimer() {
      this.refreshTime = new Date().getTime() + REFRESH_TIME;
      this.refreshTimerOn = true;
    },

    stopRefreshTimer() {
      this.refreshTimerOn = false;
    },

    startContactSupportTimer() {
      this.contactSupportTime = new Date().getTime() + CONTACT_SUPPORT_TIME;
      this.contactSupportTimerOn = true;
    },

    stopContactSupportTimer() {
      this.contactSupportTimerOn = false;
    },

    goToHome() {
      ActivityNavigator.navigateTo(Activities.HOME);
    },

    goToLoanDetails() {
      ActivityNavigator.navigateTo(Activities.LOAN_DETAILS);
    }
  },

  created() {
    if (EzUtils.isValidId(LoanScopeDataHolder.getLoanId())
      || LoanScopeDataHolder.getLoanAccountNumber()
    ) {
      LoanScopeDataHolder.setCurrentLoanTask(null);
      LoanScopeDataHolder.setWaiting(true);
      this.startContactSupportTimer();
      this.startRefreshTimer();
    } else {
      this.goToHome();
    }
  },

  computed: {
    loanAccountNumber() {
      return LoanScopeDataHolder.getLoanAccountNumber();
    },

    contactSupportDisabledButtonStyle() {
      return { "background-color": "#D0D0D0" };
    },

    contactSupportDisabledTextStyle() {
      return { color: "#FFFFFF" };
    },

    processingText() {
      return LoanScopeDataHolder.isOnboardingLoan() ? "Processing merchant onboarding"
      : "Processing Your Loan Application...";
    }
  }
};
</script>

<style scoped src="../../assets/style/base.css"></style>

